import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Activity,
  DollarSign,
  ShoppingCart,
  TrendingUp,
  Users,
  Zap,
  Search,
  Edit,
  Save,
  RefreshCw,
  Shield,
  LifeBuoy,
  MessageCircle,
  CheckCircle2,
  Clock,
  AlertCircle,
  Send,
  User as UserIcon,
  Loader2,
  X,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  BookOpen,
  Share2,
  Plus,
  Trash2,
  Wallet,
  Copy,
  RotateCcw,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCurrency } from "@/lib/currency";
import { useI18n } from "@/lib/i18n";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { copyToClipboard } from "@/lib/utils";
import { getServiceName } from "@/lib/hero_services";
import { formatServerDateTime, parseServerDate } from "@/lib/server-time";
import { SocialIcon } from "@/components/social-icons";
import { AdminApiTab } from "@/components/admin-api-tab";
import { AdminPaymentsTab } from "@/components/admin-payments-tab";

type ServicePricingRow = {
  id: number;
  name: string;
  code: string;
  price: number;
  price_min: number;
  manual_price: number | null;
  multiplier: number;
  is_popular: boolean;
  is_manual: boolean;
};

const VALID_TABS = [
  "sms",
  "emails",
  "countries",
  "provider",
  "users",
  "orders",
  "tickets",
  "accounts",
  "account_products",
  "referrals",
  "referral_withdrawals",
  "api",
  "payments",
  "audit",
];

export const Route = createFileRoute("/_app/admin/$tab")({
  component: AdminPage,
  head: () => ({
    meta: [
      { title: "Admin panel — SmsEmpire" },
      { name: "description", content: "Manage services, pricing and stats." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function AdminPage() {
  const { t } = useI18n();
  const { format } = useCurrency();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { tab: rawTab } = Route.useParams();

  const activeTab = VALID_TABS.includes(rawTab) ? rawTab : "sms";

  useEffect(() => {
    if (!VALID_TABS.includes(rawTab)) {
      navigate({ to: "/admin/$tab", params: { tab: "sms" }, replace: true });
    }
  }, [rawTab, navigate]);

  const [sms, setSms] = useState<ServicePricingRow[]>([]);
  const [emails, setEmails] = useState<ServicePricingRow[]>([]);
  const [pricingConfig, setPricingConfig] = useState<Record<string, string>>({});
  const [configDraft, setConfigDraft] = useState<Record<string, string>>({});
  const [simulation, setSimulation] = useState<{
    rows: any[];
    up: number;
    down: number;
    unchanged: number;
  } | null>(null);
  const [countrySettings, setCountrySettings] = useState<any[]>([]);
  const [countrySearch, setCountrySearch] = useState("");
  const [providerHealth, setProviderHealth] = useState<any | null>(null);
  const [auditLog, setAuditLog] = useState<any[]>([]);
  const [adminStats, setAdminStats] = useState({
    revenue: 0,
    recharges: 0,
    sales_revenue: 0,
    provider_cost: 0,
    margin: 0,
    margin_pct: 0,
    sales_with_known_cost: 0,
    sales_total: 0,
    refunded_total: 0,
    orders: 0,
    users: 0,
    hero_balance: null as number | null,
    hero_balance_low: false,
    hero_balance_min: null as number | null,
    open_tickets: 0,
    open_tickets_awaiting: 0,
    stats_since: null as string | null,
    by_service: [] as any[],
  });
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [adminUsers, setAdminUsers] = useState<any[]>([]);
  const [adminTickets, setAdminTickets] = useState<any[]>([]);
  const [adminAccounts, setAdminAccounts] = useState<any[]>([]);
  const [accountProducts, setAccountProducts] = useState<any[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null);
  const [ticketReply, setTicketReply] = useState("");
  const [ticketReplying, setTicketReplying] = useState(false);
  const [smsSearch, setSmsSearch] = useState("");
  const [emailSearch, setEmailSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [sortConfig, setSortConfig] = useState<{ key: string | null; dir: "asc" | "desc" | null }>({
    key: null,
    dir: null,
  });
  const [userSortConfig, setUserSortConfig] = useState<{
    key: string | null;
    dir: "asc" | "desc" | null;
  }>({ key: null, dir: null });
  const [orderSortConfig, setOrderSortConfig] = useState<{
    key: string | null;
    dir: "asc" | "desc" | null;
  }>({ key: null, dir: null });
  const [accountSortConfig, setAccountSortConfig] = useState<{
    key: string | null;
    dir: "asc" | "desc" | null;
  }>({ key: null, dir: null });
  const [productSortConfig, setProductSortConfig] = useState<{
    key: string | null;
    dir: "asc" | "desc" | null;
  }>({ key: null, dir: null });
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [newProduct, setNewProduct] = useState({
    code: "",
    name: "",
    description: "",
    price_usd: 5.0,
    stock: 50,
    icon_name: "Instagram",
    color: "#E4405F",
    has_followers: true,
  });
  const [multiplier, setMultiplier] = useState("1");
  const [savedMultiplier, setSavedMultiplier] = useState(1);
  // Emails carry their own margin, tuned apart from numbers.
  const [emailMultiplier, setEmailMultiplier] = useState("1");
  const [savedEmailMultiplier, setSavedEmailMultiplier] = useState(1);
  const [loading, setLoading] = useState(false);
  const ticketMessagesEndRef = useRef<HTMLDivElement>(null);
  const ticketMessagesBoxRef = useRef<HTMLDivElement>(null);
  // Open a ticket at its latest message, and stay there as replies arrive. Scrolls the
  // message box only — scrollIntoView would also jump the whole page.
  const selectedTicketId = selectedTicket?.id;
  const selectedTicketMessageCount = selectedTicket?.messages?.length ?? 0;
  useEffect(() => {
    const box = ticketMessagesBoxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [selectedTicketId, selectedTicketMessageCount]);

  // The multiplier used to be inferred by dividing the price of whichever service
  // happened to be first — which showed the wrong number as soon as that service had a
  // manual floor. It now comes from /api/admin/pricing/config, which is the value the
  // engine actually applies.

  const makeSortHandler =
    (
      setter: React.Dispatch<
        React.SetStateAction<{ key: string | null; dir: "asc" | "desc" | null }>
      >,
    ) =>
    (key: string) => {
      setter((prev) => {
        if (prev.key === key) {
          if (prev.dir === "asc") return { key, dir: "desc" };
          if (prev.dir === "desc") return { key: null, dir: null };
          return { key, dir: "asc" };
        }
        return { key, dir: "asc" };
      });
    };

  const handleSort = makeSortHandler(setSortConfig);
  const handleSortUser = makeSortHandler(setUserSortConfig);
  const handleSortOrder = makeSortHandler(setOrderSortConfig);
  const handleSortAccount = makeSortHandler(setAccountSortConfig);
  const handleSortProduct = makeSortHandler(setProductSortConfig);

  useEffect(() => {
    if (user && user.role !== "admin") navigate({ to: "/dashboard" });
  }, [user, navigate]);

  const [refSettings, setRefSettings] = useState({
    referral_reward_amount: 1,
    referral_min_recharge: 3,
    referral_min_withdrawal: 5,
    referral_enabled: true,
  });
  const [savingRefSettings, setSavingRefSettings] = useState(false);
  const [withdrawals, setWithdrawals] = useState<any[]>([]);
  const [withdrawalFilter, setWithdrawalFilter] = useState("pending");
  const [processingWithdrawal, setProcessingWithdrawal] = useState<number | null>(null);

  const handleSaveRefSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingRefSettings(true);
    try {
      const data = await api.post<any>("/api/admin/referral-settings", refSettings);
      toast.success(t("admin.saved_toast"));
      setRefSettings({
        referral_reward_amount: data.referral_reward_amount,
        referral_min_recharge: data.referral_min_recharge,
        referral_min_withdrawal: data.referral_min_withdrawal,
        referral_enabled: data.referral_enabled,
      });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("common.error"));
    } finally {
      setSavingRefSettings(false);
    }
  };

  const loadWithdrawals = useCallback(async (status: string) => {
    const d = await api.get<any[]>(`/api/admin/referral-withdrawals?status=${status}`);
    if (Array.isArray(d)) setWithdrawals(d);
  }, []);

  const handleWithdrawalAction = async (id: number, action: "approve" | "reject") => {
    // Rejecting refunds the user's referral wallet, so it should not happen by accident.
    let reason = "";
    if (action === "reject") {
      const input = window.prompt("Motivo del rechazo (se le mostrará al usuario):", "");
      if (input === null) return;
      reason = input;
    } else if (!window.confirm("¿Confirmas que ya enviaste el USDT a esa wallet?")) {
      return;
    }
    setProcessingWithdrawal(id);
    try {
      await api.post(
        `/api/admin/referral-withdrawals/${id}/${action}`,
        action === "reject" ? { reason } : undefined,
      );
      toast.success(
        action === "approve" ? "Retiro marcado como pagado" : "Retiro rechazado y reembolsado",
      );
      await loadWithdrawals(withdrawalFilter);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("common.error"));
    } finally {
      setProcessingWithdrawal(null);
    }
  };

  // Loaders split by tab. The panel used to fire all nine requests on mount, so
  // opening Tickets also pulled users, orders, purchased accounts, the service
  // catalogue and the upstream balance.
  const loadStats = useCallback(async () => {
    const [stats, hero] = await Promise.allSettled([
      api.get<any>("/api/admin/stats"),
      api.get<any>("/api/balance/hero"),
    ]);
    if (stats.status === "fulfilled" && stats.value?.revenue !== undefined)
      setAdminStats(stats.value);
    // Upstream balance depends on the provider being reachable; never let it block.
    if (hero.status === "fulfilled" && hero.value?.balance !== undefined) {
      setAdminStats((prev) => ({
        ...prev,
        hero_balance: hero.value.balance,
        hero_balance_low: Boolean(hero.value.balance_low),
        hero_balance_min: hero.value.balance_min ?? null,
      }));
    }
  }, []);

  const TAB_LOADERS: Record<string, () => Promise<void>> = useMemo(() => {
    // SMS services and email domains are both rows of the same catalogue, split by
    // category so each pricing tab only shows its own.
    const loadServicePricing = async () => {
      const [d, cfg] = await Promise.all([
        api.get<any[]>("/api/services?category=all"),
        api.get<any>("/api/admin/pricing/config"),
      ]);
      if (Array.isArray(d)) {
        const rows = d.map((s: any) => ({
          id: s.id,
          name: s.name,
          code: s.code,
          price: s.price_available,
          price_min: s.price_min,
          manual_price: s.manual_price ?? null,
          multiplier: s.price_multiplier ?? 0,
          is_popular: s.is_popular,
          is_manual: s.is_manual ?? false,
          category: s.category,
        }));
        setSms(rows.filter((s) => s.category !== "email"));
        setEmails(rows.filter((s) => s.category === "email"));
      }
      if (cfg?.config) {
        setPricingConfig(cfg.config);
        setConfigDraft(cfg.config);
        setMultiplier(String(cfg.config.global_multiplier ?? "1"));
        setSavedMultiplier(Number(cfg.config.global_multiplier ?? 1));
        const em = cfg.config.email_multiplier ?? cfg.config.global_multiplier ?? "1";
        setEmailMultiplier(String(em));
        setSavedEmailMultiplier(Number(em));
      }
    };

    return {
      sms: loadServicePricing,
      emails: loadServicePricing,
      countries: async () => {
        const d = await api.get<any[]>("/api/admin/pricing/countries");
        if (Array.isArray(d)) setCountrySettings(d);
      },
      provider: async () => {
        setProviderHealth(await api.get<any>("/api/admin/provider"));
      },
      audit: async () => {
        const d = await api.get<any[]>("/api/admin/audit-log");
        if (Array.isArray(d)) setAuditLog(d);
      },
      users: async () => {
        const d = await api.get<any[]>("/api/admin/users");
        if (Array.isArray(d)) setAdminUsers(d);
      },
      orders: async () => {
        const d = await api.get<any[]>("/api/admin/orders");
        if (Array.isArray(d)) setRecentOrders(d);
      },
      tickets: async () => {
        const d = await api.get<any[]>("/api/admin/tickets");
        if (Array.isArray(d)) setAdminTickets(d);
      },
      accounts: async () => {
        const d = await api.get<any[]>("/api/admin/purchased-accounts");
        if (Array.isArray(d)) setAdminAccounts(d);
      },
      account_products: async () => {
        const d = await api.get<any[]>("/api/accounts/products");
        if (Array.isArray(d)) setAccountProducts(d);
      },
      referrals: async () => {
        const d = await api.get<any>("/api/admin/referral-settings");
        if (d?.referral_reward_amount !== undefined) setRefSettings(d);
      },
      referral_withdrawals: () => loadWithdrawals(withdrawalFilter),
    };
  }, [loadWithdrawals, withdrawalFilter]);

  /** Reload the current tab (used after any mutation). */
  const fetchAll = useCallback(() => {
    if (!user || user.role !== "admin") return;
    void loadStats().catch(() => {});
    void TAB_LOADERS[activeTab]?.().catch((e) => {
      console.error(`Admin tab "${activeTab}" failed to load`, e);
      toast.error(t("common.error"));
    });
    // The badges on the tab strip come from counts the other tabs own, so those two
    // lists stay eagerly loaded — they are what the numbers are counting.
    if (activeTab !== "orders")
      void api
        .get<any[]>("/api/admin/orders")
        .then((d) => {
          if (Array.isArray(d)) setRecentOrders(d);
        })
        .catch(() => {});
    if (activeTab !== "accounts")
      void api
        .get<any[]>("/api/admin/purchased-accounts")
        .then((d) => {
          if (Array.isArray(d)) setAdminAccounts(d);
        })
        .catch(() => {});
  }, [user, activeTab, loadStats, TAB_LOADERS, t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /** Start revenue and margin again from now. Deletes nothing — see /api/admin/stats/reset. */
  const handleResetStats = async () => {
    if (!window.confirm(t("admin.stats_reset_confirm"))) return;
    try {
      await api.post("/api/admin/stats/reset");
      toast.success(t("admin.stats_reset_done"));
      await loadStats();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("common.error"));
    }
  };

  if (!user || user.role !== "admin") return null;

  const stats = [
    {
      // Money that actually came in: completed top-ups. Activations only move balance
      // the user had already paid for, so counting them here billed the same dollar twice.
      icon: DollarSign,
      label: t("admin.stats.revenue"),
      value: format(adminStats.revenue),
      tint: "text-primary bg-primary/10",
    },
    {
      // Turnover minus what the provider charged us. The panel used to show only the
      // first number and call it revenue.
      icon: TrendingUp,
      label: t("admin.revenue_margin"),
      value: format(adminStats.margin ?? 0),
      tint: "text-emerald-500 bg-emerald-500/10",
    },
    {
      icon: ShoppingCart,
      label: t("admin.stats.orders"),
      value: String(adminStats.orders),
      tint: "text-accent bg-accent/10",
    },
    {
      icon: Users,
      label: t("admin.stats.users"),
      value: String(adminStats.users),
      tint: "text-green-500 bg-green-500/10",
    },
    {
      icon: Zap,
      label: t("admin.hero_balance"),
      value: adminStats.hero_balance !== null ? `$${adminStats.hero_balance}` : "...",
      // Red while below the "low balance" threshold, on every tab — the warning used
      // to live only inside the Provider tab, so nobody ever saw it.
      tint: adminStats.hero_balance_low
        ? "text-red-500 bg-red-500/10"
        : "text-blue-500 bg-blue-500/10",
      alert: adminStats.hero_balance_low
        ? t("admin.balance_below_min", { min: `$${adminStats.hero_balance_min ?? ""}` })
        : null,
    },
    {
      icon: LifeBuoy,
      label: t("admin.open_tickets"),
      value: String(adminStats.open_tickets ?? 0),
      tint: "text-orange-400 bg-orange-500/10",
    },
  ];

  const handleSaveSmsPrice = async (id: number, price: number) => {
    // The manual price is a floor now, not a fixed price: the multiplier still applies
    // on top for countries that cost more than it.
    try {
      const res = await api.post<any>(`/api/admin/services/${id}`, { priceUsd: price });
      const apply = (prev: ServicePricingRow[]) =>
        prev.map((s) =>
          s.id === id
            ? { ...s, is_manual: true, manual_price: price, price: res?.price_available ?? s.price }
            : s,
        );
      setSms(apply);
      setEmails(apply);
      toast.success(t("admin.saved_toast"));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("common.error"));
    }
  };

  const handleClearServiceFloor = async (id: number) => {
    try {
      const res = await api.post<any>(`/api/admin/services/${id}`, { is_manual: false });
      const apply = (prev: ServicePricingRow[]) =>
        prev.map((s) =>
          s.id === id
            ? { ...s, is_manual: false, manual_price: null, price: res?.price_available ?? s.price }
            : s,
        );
      setSms(apply);
      setEmails(apply);
      toast.success(t("admin.saved_toast"));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("common.error"));
    }
  };

  const handleTogglePopular = async (id: number, current: boolean) => {
    try {
      await api.post(`/api/admin/services/${id}`, { is_popular: !current });
      setSms((prev) => prev.map((s) => (s.id === id ? { ...s, is_popular: !current } : s)));
      toast.success(t("admin.saved_toast"));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("common.error"));
    }
  };

  const handleSimulate = async (mode: "all" | "non_manual" = "all") => {
    const m = parseFloat(multiplier.replace(",", "."));
    if (isNaN(m) || m <= 0) {
      toast.error(t("admin.invalid_multiplier"));
      return;
    }
    try {
      // Nothing is written: this is the preview the panel never had before a bulk
      // price change.
      setSimulation(
        await api.post<any>("/api/admin/pricing/simulate", {
          config: { ...configDraft, global_multiplier: String(m) },
          mode,
        }),
      );
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("common.error"));
    }
  };

  const handleSaveConfig = async () => {
    try {
      const res = await api.post<any>("/api/admin/pricing/config", { config: configDraft });
      setPricingConfig(res.config);
      setConfigDraft(res.config);
      setSimulation(null);
      toast.success(t("admin.saved_toast"));
      fetchAll();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("common.error"));
    }
  };

  const handleSaveCountry = async (countryId: number, patch: Record<string, unknown>) => {
    try {
      await api.post("/api/admin/pricing/countries", { country_id: countryId, ...patch });
      setCountrySettings((prev) =>
        prev.map((c) => (c.country_id === countryId ? { ...c, ...patch } : c)),
      );
      toast.success(t("admin.saved_toast"));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("common.error"));
    }
  };

  const handleCircuitReset = async (pair: string) => {
    const [service_code, country_id] = pair.split(":");
    try {
      await api.post("/api/admin/provider/circuit-reset", {
        service_code,
        country_id: Number(country_id),
      });
      setProviderHealth((prev: any) =>
        prev
          ? { ...prev, circuit_open: prev.circuit_open.filter((p: string) => p !== pair) }
          : prev,
      );
      toast.success(t("admin.saved_toast"));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("common.error"));
    }
  };

  const handleRefreshPrices = async () => {
    setLoading(true);
    try {
      await api.post("/api/admin/provider/refresh-prices");
      toast.success(t("admin.saved_toast"));
      fetchAll();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleApplyMultiplier = async (
    mode: "all" | "non_manual" = "all",
    category: "sms" | "email" = "sms",
  ) => {
    const raw = category === "email" ? emailMultiplier : multiplier;
    const m = parseFloat(raw.replace(",", "."));
    if (isNaN(m) || m <= 0) {
      toast.error(t("admin.invalid_multiplier"));
      return;
    }
    setLoading(true);
    try {
      const data = await api.post<any>("/api/admin/services/price-multiplier", {
        multiplier: m,
        mode,
        category,
      });
      const label =
        mode === "non_manual"
          ? t("admin.only_non_manual").replace("{m}", String(m))
          : t("admin.apply_to_all").replace("{m}", String(m));
      toast.success(
        `${label} — ${t("admin.services_updated").replace("{updated}", String(data.updated))}`,
      );
      if (category === "email") setSavedEmailMultiplier(m);
      else setSavedMultiplier(m);
      setSimulation(null);
      fetchAll();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("admin.error_multiplier"));
    } finally {
      setLoading(false);
    }
  };

  const handleClearManual = async (category: "sms" | "email" = "sms") => {
    const raw = category === "email" ? emailMultiplier : multiplier;
    const m = parseFloat(raw.replace(",", "."));
    if (isNaN(m) || m <= 0) {
      toast.error(t("admin.invalid_multiplier"));
      return;
    }
    const manualCount = (category === "email" ? emails : sms).filter((s) => s.is_manual).length;
    if (manualCount === 0) {
      toast.info(t("admin.no_manual_active"));
      return;
    }
    setLoading(true);
    try {
      const data = await api.post<any>("/api/admin/services/clear-manual", {
        multiplier: m,
        category,
      });
      toast.success(
        t("admin.manual_cleared")
          .replace("{cleared}", String(data.cleared))
          .replace("{m}", String(m)),
      );
      fetchAll();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("admin.error_clear_manual"));
    } finally {
      setLoading(false);
    }
  };

  /** Post a reply to the open ticket and refresh it. Shared by Enter and the button. */
  const sendTicketReply = async () => {
    if (!ticketReply.trim() || !selectedTicket) return;
    setTicketReplying(true);
    try {
      await api.post(`/api/tickets/${selectedTicket.id}/messages`, { content: ticketReply });
      setTicketReply("");
      fetchAll();
      setSelectedTicket(await api.get<any>(`/api/tickets/${selectedTicket.id}`));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("common.error"));
    } finally {
      setTicketReplying(false);
    }
  };

  const handleUserUpdate = async (
    userId: number,
    update: {
      role?: string;
      balance_add?: number;
      is_banned?: boolean;
      is_email_verified?: boolean;
    },
  ) => {
    try {
      const updated = await api.patch<any>(`/api/admin/users/${userId}`, update);
      setAdminUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, ...updated } : u)));
      toast.success(t("admin.saved"));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("admin.error_update_user"));
    }
  };

  const handleProductUpdate = async (productId: number, field: string, value: any) => {
    try {
      await api.put(`/api/admin/accounts/products/${productId}`, { [field]: value });
      toast.success(t("admin.saved"));
      fetchAll();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("admin.error_update"));
    }
  };

  const handleProductCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProduct.code || !newProduct.name || !newProduct.description) {
      toast.error(t("admin.fill_required"));
      return;
    }
    try {
      await api.post("/api/admin/accounts/products", newProduct);
      toast.success(t("admin.saved"));
      setShowAddProductModal(false);
      setNewProduct({
        code: "",
        name: "",
        description: "",
        price_usd: 5.0,
        stock: 50,
        icon_name: "Instagram",
        color: "#E4405F",
        has_followers: true,
      });
      fetchAll();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("common.error"));
    }
  };

  const handleProductDelete = async (productId: number) => {
    if (!confirm("¿Seguro que deseas eliminar este producto de cuenta?")) return;
    try {
      await api.delete(`/api/admin/accounts/products/${productId}`);
      toast.success(t("admin.saved"));
      fetchAll();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("common.error"));
    }
  };

  const filteredSms = sms
    .filter(
      (s) =>
        s.name.toLowerCase().includes(smsSearch.toLowerCase()) ||
        s.code.toLowerCase().includes(smsSearch.toLowerCase()),
    )
    .sort((a, b) => {
      if (!sortConfig.key || !sortConfig.dir) return 0;
      const dirMult = sortConfig.dir === "asc" ? 1 : -1;
      if (sortConfig.key === "service") return a.name.localeCompare(b.name) * dirMult;
      if (sortConfig.key === "code") return a.code.localeCompare(b.code) * dirMult;
      if (sortConfig.key === "hero_price") return (a.price_min - b.price_min) * dirMult;
      if (sortConfig.key === "my_price") return (a.price - b.price) * dirMult;
      if (sortConfig.key === "is_manual")
        return (Number(b.is_manual) - Number(a.is_manual)) * dirMult;
      if (sortConfig.key === "popular")
        return (Number(b.is_popular) - Number(a.is_popular)) * dirMult;
      return 0;
    });

  const filteredEmails = emails
    .filter(
      (s) =>
        s.name.toLowerCase().includes(emailSearch.toLowerCase()) ||
        s.code.toLowerCase().includes(emailSearch.toLowerCase()),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const filteredUsers = adminUsers
    .filter(
      (u) =>
        u.username?.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.email?.toLowerCase().includes(userSearch.toLowerCase()),
    )
    .sort((a, b) => {
      if (!userSortConfig.key || !userSortConfig.dir) return 0;
      const mult = userSortConfig.dir === "asc" ? 1 : -1;
      if (userSortConfig.key === "username")
        return (a.username || "").localeCompare(b.username || "") * mult;
      if (userSortConfig.key === "email")
        return (a.email || "").localeCompare(b.email || "") * mult;
      if (userSortConfig.key === "balance") return (a.balance - b.balance) * mult;
      if (userSortConfig.key === "role") return (a.role || "").localeCompare(b.role || "") * mult;
      return 0;
    });

  const filteredOrders = [...recentOrders].sort((a, b) => {
    if (!orderSortConfig.key || !orderSortConfig.dir) return 0;
    const mult = orderSortConfig.dir === "asc" ? 1 : -1;
    if (orderSortConfig.key === "id") return (Number(a.id) - Number(b.id)) * mult;
    if (orderSortConfig.key === "type") return (a.type || "").localeCompare(b.type || "") * mult;
    if (orderSortConfig.key === "user") return (a.user || "").localeCompare(b.user || "") * mult;
    if (orderSortConfig.key === "total") return (a.total - b.total) * mult;
    // Unknown cost/profit (sales from before costs were recorded) sorts as lowest.
    if (["paid", "cost", "profit"].includes(orderSortConfig.key))
      return (
        ((a[orderSortConfig.key] ?? -Infinity) - (b[orderSortConfig.key] ?? -Infinity) || 0) * mult
      );
    if (orderSortConfig.key === "status")
      return (a.status || "").localeCompare(b.status || "") * mult;
    if (orderSortConfig.key === "date")
      return ((parseServerDate(a.created_at) || 0) - (parseServerDate(b.created_at) || 0)) * mult;
    return 0;
  });

  const filteredAccounts = [...adminAccounts].sort((a, b) => {
    if (!accountSortConfig.key || !accountSortConfig.dir) return 0;
    const mult = accountSortConfig.dir === "asc" ? 1 : -1;
    if (accountSortConfig.key === "id") return (a.id - b.id) * mult;
    if (accountSortConfig.key === "user")
      return (
        (a.username || `User ${a.user_id}`).localeCompare(b.username || `User ${b.user_id}`) * mult
      );
    if (accountSortConfig.key === "account_type")
      return (a.account_type || "").localeCompare(b.account_type || "") * mult;
    if (accountSortConfig.key === "credentials")
      return (a.account_data || "").localeCompare(b.account_data || "") * mult;
    if (accountSortConfig.key === "notes")
      return (a.notes || "").localeCompare(b.notes || "") * mult;
    if (accountSortConfig.key === "status")
      return (a.status || "").localeCompare(b.status || "") * mult;
    if (accountSortConfig.key === "date")
      return ((parseServerDate(a.created_at) || 0) - (parseServerDate(b.created_at) || 0)) * mult;
    return 0;
  });

  const filteredProducts = [...accountProducts].sort((a, b) => {
    if (!productSortConfig.key || !productSortConfig.dir) return 0;
    const mult = productSortConfig.dir === "asc" ? 1 : -1;
    if (productSortConfig.key === "code") return (a.code || "").localeCompare(b.code || "") * mult;
    if (productSortConfig.key === "name") return (a.name || "").localeCompare(b.name || "") * mult;
    if (productSortConfig.key === "price_usd") return (a.price_usd - b.price_usd) * mult;
    if (productSortConfig.key === "stock") return (a.stock - b.stock) * mult;
    if (productSortConfig.key === "has_followers")
      return (Number(b.has_followers) - Number(a.has_followers)) * mult;
    return 0;
  });

  const pendingAccountsCount = adminAccounts.filter((a: any) => a.status === "pending").length;
  const pendingOrdersCount = recentOrders.filter((o: any) => o.status === "pending").length;

  // Totals of the lines on screen. Lines with an unknown cost (sold before costs were
  // recorded) are left out of cost and profit rather than counted as free, and the
  // footer says how many there are.
  const orderTotals = recentOrders.reduce(
    (acc, o) => {
      acc.paid += Number(o.paid ?? o.total ?? 0);
      if (o.cost == null) {
        acc.unknownCost += 1;
      } else {
        acc.cost += Number(o.cost);
        acc.profit += Number(o.profit ?? 0);
      }
      return acc;
    },
    { paid: 0, cost: 0, profit: 0, unknownCost: 0 },
  );

  const [deliveringAccount, setDeliveringAccount] = useState<any | null>(null);

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      {deliveringAccount && (
        <SendAccountModal
          account={deliveringAccount}
          onClose={() => setDeliveringAccount(null)}
          onDelivered={fetchAll}
        />
      )}

      {/* Responsive Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("admin.title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("admin.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <Button asChild variant="outline" size="sm">
            <Link to="/blog">
              <BookOpen className="h-4 w-4 mr-2" />
              {t("admin.open_blog")}
            </Link>
          </Button>
          <Button variant="outline" size="sm" onClick={fetchAll}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t("admin.refresh")}
          </Button>
        </div>
      </div>

      {/* Responsive Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.label} className="flex items-center gap-4 border-border bg-card p-5">
              <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${s.tint}`}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground truncate">{s.label}</p>
                <p className={`text-xl font-bold truncate ${s.alert ? "text-red-500" : ""}`}>
                  {s.value}
                </p>
                {s.alert && <p className="text-xs text-red-400 truncate">{s.alert}</p>}
              </div>
            </Card>
          );
        })}
      </div>

      {/* What window the two money cards above are counting, and how to start it over. */}
      <div className="-mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span>
          {adminStats.stats_since
            ? t("admin.stats_since", {
                date: formatServerDateTime(adminStats.stats_since),
              })
            : t("admin.stats_since_always")}
        </span>
        <button
          type="button"
          onClick={handleResetStats}
          className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-foreground"
        >
          <RotateCcw className="h-3 w-3" />
          {t("admin.stats_reset")}
        </button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(val) => {
          navigate({ to: "/admin/$tab", params: { tab: val } });
        }}
        className="space-y-4"
      >
        <TabsList className="flex flex-wrap gap-1.5 h-auto p-1 max-w-full overflow-x-auto">
          <TabsTrigger value="sms">{t("admin.sms_pricing")}</TabsTrigger>
          <TabsTrigger value="emails">{t("admin.tab_emails")}</TabsTrigger>
          <TabsTrigger value="countries">{t("admin.countries_tab")}</TabsTrigger>
          <TabsTrigger value="provider" className="relative">
            {t("admin.provider_tab")}
            {(providerHealth?.balance_low || adminStats.hero_balance_low) && (
              <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-red-500" />
            )}
          </TabsTrigger>
          <TabsTrigger value="users">{t("admin.users")}</TabsTrigger>
          <TabsTrigger value="orders" className="relative">
            {t("admin.recent_orders")}
            {pendingOrdersCount > 0 && (
              <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                {pendingOrdersCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="tickets" className="relative">
            {t("admin.tickets_tab")}
            {adminStats.open_tickets_awaiting > 0 ? (
              <span className="ml-1.5 rounded-full bg-orange-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                {adminStats.open_tickets_awaiting}
              </span>
            ) : adminStats.open_tickets > 0 ? (
              <span className="ml-1.5 rounded-full bg-muted-foreground/40 px-1.5 py-0.5 text-[10px] font-bold text-white">
                {adminStats.open_tickets}
              </span>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="accounts" className="relative">
            {t("admin.purchased_accounts")}
            {pendingAccountsCount > 0 && (
              <span className="ml-1.5 rounded-full bg-purple-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                {pendingAccountsCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="account_products">{t("admin.account_products")}</TabsTrigger>
          <TabsTrigger value="referrals">{t("admin.referrals")}</TabsTrigger>
          <TabsTrigger value="referral_withdrawals">{t("admin.withdrawals")}</TabsTrigger>
          <TabsTrigger value="api">API</TabsTrigger>
          <TabsTrigger value="payments">{t("admin.tab_payments")}</TabsTrigger>
          <TabsTrigger value="audit">{t("admin.audit_log")}</TabsTrigger>
        </TabsList>

        {/* ── Public API Tab (PLAN_API.md §9) ── */}
        <TabsContent value="api">{activeTab === "api" && <AdminApiTab />}</TabsContent>

        {/* ── Payment methods ── */}
        <TabsContent value="payments">
          {activeTab === "payments" && <AdminPaymentsTab />}
        </TabsContent>

        {/* ── SMS / Services Tab ── */}
        <TabsContent value="sms">
          <Card className="border-border bg-card p-4 sm:p-6 space-y-4">
            {/* Multiplier */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-xl border border-border bg-background">
              <div className="flex items-start gap-3">
                <Zap className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium mb-0.5">{t("admin.global_multiplier")}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("admin.global_multiplier_desc")}
                  </p>
                  {sms.some((s) => s.is_manual) && (
                    <p className="text-xs text-yellow-500 mt-1">
                      {t("admin.manual_active_warning", {
                        count: sms.filter((s) => s.is_manual).length,
                      })}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={multiplier}
                  onChange={(e) => setMultiplier(e.target.value)}
                  className="w-24"
                  placeholder={t("common.example_value", { value: "10" })}
                />
                <span className="text-sm text-muted-foreground shrink-0">
                  {t("admin.price_base")}
                </span>
                <Button onClick={() => handleSimulate("all")} size="sm" variant="secondary">
                  {t("admin.simulate")}
                </Button>
                {sms.some((s) => s.is_manual) ? (
                  <>
                    <Button
                      onClick={() => handleApplyMultiplier("all")}
                      disabled={loading}
                      size="sm"
                      variant="default"
                    >
                      {t(loading ? "admin.applying" : "admin.apply_all")}
                    </Button>
                    <Button
                      onClick={() => handleApplyMultiplier("non_manual")}
                      disabled={loading}
                      size="sm"
                      variant="outline"
                    >
                      {t(loading ? "admin.applying" : "admin.apply_non_manual")}
                    </Button>
                    <Button
                      onClick={() => handleClearManual("sms")}
                      disabled={loading}
                      size="sm"
                      variant="destructive"
                    >
                      {t(loading ? "admin.clearing" : "admin.clear_manual")}
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => handleApplyMultiplier("all")} disabled={loading} size="sm">
                    {t(loading ? "admin.applying" : "admin.apply")}
                  </Button>
                )}
              </div>
            </div>

            {/* What the multiplier would do, before it is applied. */}
            {simulation && (
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
                <p className="text-sm font-medium">
                  {t("admin.simulate_result", {
                    up: simulation.up,
                    down: simulation.down,
                    unchanged: simulation.unchanged,
                  })}
                </p>
                <div className="mt-2 max-h-56 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="text-muted-foreground">
                      <tr>
                        <th className="py-1 text-left">{t("admin.service")}</th>
                        <th className="py-1 text-right">{t("admin.cost")}</th>
                        <th className="py-1 text-right">{t("admin.your_price")}</th>
                        <th className="py-1 text-right">→</th>
                      </tr>
                    </thead>
                    <tbody>
                      {simulation.rows.map((r: any) => (
                        <tr key={r.code} className="border-t border-border/50">
                          <td className="py-1">{r.name}</td>
                          <td className="py-1 text-right text-muted-foreground">
                            ${r.provider_cost.toFixed(4)}
                          </td>
                          <td className="py-1 text-right text-muted-foreground line-through">
                            ${r.old_price.toFixed(2)}
                          </td>
                          <td
                            className={`py-1 text-right font-semibold ${
                              r.delta > 0 ? "text-green-500" : r.delta < 0 ? "text-red-400" : ""
                            }`}
                          >
                            ${r.new_price.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Engine configuration */}
            <div className="rounded-xl border border-border bg-background p-4">
              <p className="text-sm font-medium">{t("admin.pricing_title")}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{t("admin.pricing_desc")}</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  ["global_multiplier", "admin.global_multiplier_label"],
                  ["min_margin_abs", "admin.min_margin"],
                  ["rounding_step", "admin.rounding_step"],
                  ["provider_balance_min", "admin.balance_alert"],
                ].map(([key, label]) => (
                  <label key={key} className="text-xs text-muted-foreground">
                    {t(label)}
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={configDraft[key] ?? ""}
                      onChange={(e) =>
                        setConfigDraft((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                      className="mt-1"
                    />
                  </label>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={handleSaveConfig} disabled={loading}>
                  {t("admin.save_config")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfigDraft(pricingConfig)}
                  disabled={loading}
                >
                  {t("common.cancel")}
                </Button>
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder={t("admin.search_service")}
                value={smsSearch}
                onChange={(e) => setSmsSearch(e.target.value)}
              />
            </div>

            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <table className="w-full text-sm min-w-[650px]">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <ThSortable sortKey="service" currentSort={sortConfig} onSort={handleSort}>
                      {t("admin.service")}
                    </ThSortable>
                    <ThSortable sortKey="code" currentSort={sortConfig} onSort={handleSort}>
                      {t("admin.code")}
                    </ThSortable>
                    <ThSortable sortKey="hero_price" currentSort={sortConfig} onSort={handleSort}>
                      {t("admin.base_hero")}
                    </ThSortable>
                    <ThSortable sortKey="my_price" currentSort={sortConfig} onSort={handleSort}>
                      {t("admin.manual_floor")}
                    </ThSortable>
                    <th className="pb-2">{t("admin.resulting_price")}</th>
                    <th className="pb-2">{t("admin.margin_pct")}</th>
                    <ThSortable sortKey="is_manual" currentSort={sortConfig} onSort={handleSort}>
                      {t("admin.change")}
                    </ThSortable>
                    <ThSortable sortKey="popular" currentSort={sortConfig} onSort={handleSort}>
                      {t("admin.popular")}
                    </ThSortable>
                    <th className="pb-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSms.map((s) => (
                    <tr key={s.id} className="border-b border-border/60">
                      <td className="py-2 font-medium">{s.name}</td>
                      <td className="py-2 font-mono text-xs text-muted-foreground">{s.code}</td>
                      <td className="py-2 text-xs text-muted-foreground">
                        ${s.price_min?.toFixed(4)}
                      </td>
                      <td className="py-2">
                        <Input
                          type="number"
                          step="0.0001"
                          min="0"
                          placeholder="—"
                          value={s.manual_price ?? ""}
                          onChange={(e) =>
                            setSms((prev) =>
                              prev.map((item) =>
                                item.id === s.id
                                  ? {
                                      ...item,
                                      manual_price:
                                        e.target.value === "" ? null : Number(e.target.value),
                                    }
                                  : item,
                              ),
                            )
                          }
                          className="w-28"
                        />
                      </td>
                      <td className="py-2 font-semibold">${s.price?.toFixed(4)}</td>
                      <td className="py-2 text-xs">
                        {s.price_min > 0 ? (
                          <span
                            className={
                              s.price > s.price_min ? "text-green-500" : "text-red-400 font-bold"
                            }
                          >
                            {(((s.price - s.price_min) / s.price_min) * 100).toFixed(0)}%
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-2">
                        {s.is_manual && (
                          <div className="flex flex-col gap-1 items-start">
                            <span className="text-yellow-500 text-xs font-bold">
                              {t("admin.manual")}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-[10px]"
                              onClick={() => handleClearServiceFloor(s.id)}
                            >
                              {t("admin.remove")}
                            </Button>
                          </div>
                        )}
                      </td>
                      <td className="py-2">
                        <button
                          onClick={() => handleTogglePopular(s.id, s.is_popular)}
                          className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                            s.is_popular
                              ? "bg-yellow-500/20 text-yellow-500"
                              : "bg-muted text-muted-foreground hover:bg-yellow-500/10"
                          }`}
                        >
                          {s.is_popular ? `⭐ ${t("admin.popular")}` : t("admin.normal")}
                        </button>
                      </td>
                      <td className="py-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={s.manual_price == null || s.manual_price <= 0}
                          onClick={() => handleSaveSmsPrice(s.id, s.manual_price as number)}
                        >
                          <Save className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ── Email domains pricing Tab ── */}
        <TabsContent value="emails">
          <Card className="p-4 sm:p-6 space-y-4">
            <div>
              <h2 className="text-lg font-semibold">{t("admin.tab_emails")}</h2>
              <p className="text-xs text-muted-foreground mt-1">{t("admin.email_pricing_hint")}</p>
            </div>

            {/* Multiplier — same controls as SMS, on its own margin */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-xl border border-border bg-background">
              <div className="flex items-start gap-3">
                <Zap className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium mb-0.5">{t("admin.email_multiplier")}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("admin.email_multiplier_desc")}
                  </p>
                  {emails.some((s) => s.is_manual) && (
                    <p className="text-xs text-yellow-500 mt-1">
                      {t("admin.manual_active_warning", {
                        count: emails.filter((s) => s.is_manual).length,
                      })}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={emailMultiplier}
                  onChange={(e) => setEmailMultiplier(e.target.value)}
                  className="w-24"
                  placeholder={t("common.example_value", { value: "10" })}
                />
                <span className="text-sm text-muted-foreground shrink-0">
                  {t("admin.price_base")}
                </span>
                {emails.some((s) => s.is_manual) ? (
                  <>
                    <Button
                      onClick={() => handleApplyMultiplier("all", "email")}
                      disabled={loading}
                      size="sm"
                    >
                      {t(loading ? "admin.applying" : "admin.apply_all")}
                    </Button>
                    <Button
                      onClick={() => handleApplyMultiplier("non_manual", "email")}
                      disabled={loading}
                      size="sm"
                      variant="outline"
                    >
                      {t(loading ? "admin.applying" : "admin.apply_non_manual")}
                    </Button>
                    <Button
                      onClick={() => handleClearManual("email")}
                      disabled={loading}
                      size="sm"
                      variant="destructive"
                    >
                      {t(loading ? "admin.clearing" : "admin.clear_manual")}
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => handleApplyMultiplier("all", "email")}
                    disabled={loading}
                    size="sm"
                  >
                    {t(loading ? "admin.applying" : "admin.apply")}
                  </Button>
                )}
              </div>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder={t("admin.search_domain")}
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
              />
            </div>

            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <table className="w-full text-sm min-w-[650px]">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="pb-2">{t("admin.domain")}</th>
                    <th className="pb-2">{t("admin.base_hero")}</th>
                    <th className="pb-2">{t("admin.manual_floor")}</th>
                    <th className="pb-2">{t("admin.resulting_price")}</th>
                    <th className="pb-2">{t("admin.margin_pct")}</th>
                    <th className="pb-2">{t("admin.change")}</th>
                    <th className="pb-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmails.map((s) => (
                    <tr key={s.id} className="border-b border-border/60">
                      <td className="py-2 font-medium">{s.name}</td>
                      <td className="py-2 text-xs text-muted-foreground">
                        ${s.price_min?.toFixed(4)}
                      </td>
                      <td className="py-2">
                        <Input
                          type="number"
                          step="0.0001"
                          min="0"
                          placeholder="—"
                          value={s.manual_price ?? ""}
                          onChange={(e) =>
                            setEmails((prev) =>
                              prev.map((item) =>
                                item.id === s.id
                                  ? {
                                      ...item,
                                      manual_price:
                                        e.target.value === "" ? null : Number(e.target.value),
                                    }
                                  : item,
                              ),
                            )
                          }
                          className="w-28"
                        />
                      </td>
                      <td className="py-2 font-semibold">${s.price?.toFixed(4)}</td>
                      <td className="py-2 text-xs">
                        {s.price_min > 0 ? (
                          <span
                            className={
                              s.price > s.price_min ? "text-green-500" : "text-red-400 font-bold"
                            }
                          >
                            {(((s.price - s.price_min) / s.price_min) * 100).toFixed(0)}%
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-2">
                        {s.is_manual && (
                          <div className="flex flex-col gap-1 items-start">
                            <span className="text-yellow-500 text-xs font-bold">
                              {t("admin.manual")}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-[10px]"
                              onClick={() => handleClearServiceFloor(s.id)}
                            >
                              {t("admin.remove")}
                            </Button>
                          </div>
                        )}
                      </td>
                      <td className="py-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={s.manual_price == null || s.manual_price <= 0}
                          onClick={() => handleSaveSmsPrice(s.id, s.manual_price as number)}
                        >
                          <Save className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {filteredEmails.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                        {t("admin.no_email_domains")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ── Countries Tab ── */}
        <TabsContent value="countries">
          <Card className="border-border bg-card p-4 sm:p-6 space-y-4">
            <div>
              <p className="text-sm font-medium">{t("admin.countries_tab")}</p>
              <p className="text-xs text-muted-foreground">
                {t("admin.success_rate")} — {t("admin.pricing_desc")}
              </p>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder={t("dashboard.search_country")}
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
              />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="pb-2">{t("admin.countries_tab")}</th>
                    <th className="pb-2">{t("admin.country_enabled")}</th>
                    <th className="pb-2">{t("admin.country_multiplier")}</th>
                    <th className="pb-2">{t("admin.country_max_cost")}</th>
                    <th className="pb-2">{t("admin.success_rate")}</th>
                  </tr>
                </thead>
                <tbody>
                  {countrySettings
                    .filter((c) =>
                      c.name?.toLowerCase().includes(countrySearch.trim().toLowerCase()),
                    )
                    .slice(0, 200)
                    .map((c) => (
                      <tr key={c.country_id} className="border-b border-border/60">
                        <td className="py-2 font-medium">
                          {c.name}{" "}
                          <span className="text-xs text-muted-foreground">#{c.country_id}</span>
                        </td>
                        <td className="py-2">
                          <button
                            onClick={() => handleSaveCountry(c.country_id, { enabled: !c.enabled })}
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                              c.enabled
                                ? "bg-green-500/20 text-green-500"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {c.enabled ? "✓" : "✕"}
                          </button>
                        </td>
                        <td className="py-2">
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            placeholder="—"
                            defaultValue={c.multiplier ?? ""}
                            onBlur={(e) =>
                              e.target.value !== String(c.multiplier ?? "") &&
                              handleSaveCountry(c.country_id, {
                                multiplier: Number(e.target.value) || undefined,
                              })
                            }
                            className="w-24"
                          />
                        </td>
                        <td className="py-2">
                          <Input
                            type="number"
                            step="0.5"
                            min="0"
                            placeholder="—"
                            defaultValue={c.max_cost_allowed ?? ""}
                            onBlur={(e) =>
                              e.target.value !== String(c.max_cost_allowed ?? "") &&
                              handleSaveCountry(c.country_id, {
                                max_cost_allowed: Number(e.target.value) || 0,
                              })
                            }
                            className="w-24"
                          />
                        </td>
                        <td className="py-2 text-xs">
                          {c.success_rate == null ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <span
                              className={
                                c.success_rate > 0.8 ? "text-green-500" : "text-yellow-500"
                              }
                            >
                              {(c.success_rate * 100).toFixed(0)}% ({c.success_count}/
                              {c.success_count + c.failure_count})
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ── Provider Tab ── */}
        <TabsContent value="provider">
          <Card className="border-border bg-card p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">{t("admin.provider_health")}</p>
              <Button size="sm" variant="outline" onClick={handleRefreshPrices} disabled={loading}>
                <RefreshCw className="mr-1 h-3 w-3" /> {t("admin.refresh_prices")}
              </Button>
            </div>

            {providerHealth && (
              <>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div
                    className={`rounded-xl border p-4 ${
                      providerHealth.balance_low
                        ? "border-red-500/40 bg-red-500/5"
                        : "border-border bg-background"
                    }`}
                  >
                    <p className="text-xs text-muted-foreground">{t("admin.upstream_balance")}</p>
                    <p className="text-xl font-bold">
                      ${providerHealth.balance?.toFixed(3) ?? "—"}
                    </p>
                    {providerHealth.balance_low && (
                      <p className="mt-1 text-xs text-red-400">
                        &lt; ${providerHealth.balance_min}
                      </p>
                    )}
                  </div>
                  <div className="rounded-xl border border-border bg-background p-4">
                    <p className="text-xs text-muted-foreground">{t("admin.max_affordable")}</p>
                    <p className="text-xl font-bold">
                      ${providerHealth.affordability_cutoff?.toFixed(3) ?? "—"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-border bg-background p-4">
                    <p className="text-xs text-muted-foreground">{t("admin.cache_age")}</p>
                    <p className="text-xl font-bold">
                      {providerHealth.caches?.balance_age_s ?? "—"}s
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {providerHealth.caches?.priced_services ?? 0} svc ·{" "}
                      {Object.keys(providerHealth.rate_limited ?? {}).length > 0
                        ? t("admin.rate_limited")
                        : "OK"}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-sm font-medium">{t("admin.circuit_open")}</p>
                  {providerHealth.circuit_open?.length ? (
                    <div className="flex flex-wrap gap-2">
                      {providerHealth.circuit_open.map((pair: string) => (
                        <span
                          key={pair}
                          className="flex items-center gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-2 py-1 text-xs"
                        >
                          {pair}
                          <button className="underline" onClick={() => handleCircuitReset(pair)}>
                            {t("admin.circuit_reset")}
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">—</p>
                  )}
                </div>

                <div>
                  <p className="mb-2 text-sm font-medium">{t("admin.worst_countries")}</p>
                  <table className="w-full text-sm">
                    <tbody>
                      {providerHealth.worst_countries?.map((c: any) => (
                        <tr key={c.country_id} className="border-b border-border/60">
                          <td className="py-1">{c.name ?? `#${c.country_id}`}</td>
                          <td className="py-1 text-right text-xs text-muted-foreground">
                            {c.success_count} ✓ / {c.failure_count} ✕
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>
        </TabsContent>

        {/* ── Audit Tab ── */}
        <TabsContent value="audit">
          <Card className="border-border bg-card p-4 sm:p-6">
            {auditLog.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("admin.audit_empty")}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="pb-2">{t("admin.date")}</th>
                      <th className="pb-2">{t("admin.audit_admin")}</th>
                      <th className="pb-2">{t("admin.change")}</th>
                      <th className="pb-2">{t("admin.audit_target")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLog.map((row) => (
                      <tr key={row.id} className="border-b border-border/60 align-top">
                        <td className="py-2 text-xs text-muted-foreground">
                          {formatServerDateTime(row.created_at)}
                        </td>
                        <td className="py-2 text-xs">{row.admin_email}</td>
                        <td className="py-2 font-mono text-xs">{row.action}</td>
                        <td className="py-2 text-xs text-muted-foreground">
                          {row.target}
                          {row.after && (
                            <span className="ml-1 opacity-70">{row.after.slice(0, 80)}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ── Users Tab ── */}
        <TabsContent value="users">
          <Card className="border-border bg-card p-4 sm:p-6 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder={t("admin.search_user")}
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
            </div>
            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <table className="w-full text-sm min-w-[650px]">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <ThSortable
                      sortKey="username"
                      currentSort={userSortConfig}
                      onSort={handleSortUser}
                    >
                      {t("admin.user")}
                    </ThSortable>
                    <ThSortable
                      sortKey="email"
                      currentSort={userSortConfig}
                      onSort={handleSortUser}
                    >
                      {t("admin.email_col")}
                    </ThSortable>
                    <ThSortable
                      sortKey="balance"
                      currentSort={userSortConfig}
                      onSort={handleSortUser}
                    >
                      {t("balance")}
                    </ThSortable>
                    <ThSortable sortKey="role" currentSort={userSortConfig} onSort={handleSortUser}>
                      {t("admin.role")}
                    </ThSortable>
                    <th className="pb-2">{t("admin.email_verified")}</th>
                    <th className="pb-2">{t("admin.add_balance")}</th>
                    <th className="pb-2">{t("admin.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <UserRow
                      key={u.id}
                      user={u}
                      onUpdate={handleUserUpdate}
                      format={format}
                      t={t}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ── Recent Orders Tab ── */}
        <TabsContent value="orders">
          <Card className="border-border bg-card p-4 sm:p-6">
            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <table className="w-full text-sm min-w-[820px]">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <ThSortable sortKey="id" currentSort={orderSortConfig} onSort={handleSortOrder}>
                      ID
                    </ThSortable>
                    <ThSortable
                      sortKey="type"
                      currentSort={orderSortConfig}
                      onSort={handleSortOrder}
                    >
                      {t("admin.type_label")}
                    </ThSortable>
                    <ThSortable
                      sortKey="user"
                      currentSort={orderSortConfig}
                      onSort={handleSortOrder}
                    >
                      {t("admin.user")}
                    </ThSortable>
                    <ThSortable
                      sortKey="paid"
                      currentSort={orderSortConfig}
                      onSort={handleSortOrder}
                    >
                      {t("admin.paid")}
                    </ThSortable>
                    <ThSortable
                      sortKey="cost"
                      currentSort={orderSortConfig}
                      onSort={handleSortOrder}
                    >
                      {t("admin.cost")}
                    </ThSortable>
                    <ThSortable
                      sortKey="profit"
                      currentSort={orderSortConfig}
                      onSort={handleSortOrder}
                    >
                      {t("admin.profit")}
                    </ThSortable>
                    <ThSortable
                      sortKey="status"
                      currentSort={orderSortConfig}
                      onSort={handleSortOrder}
                    >
                      {t("admin.status")}
                    </ThSortable>
                    <ThSortable
                      sortKey="date"
                      currentSort={orderSortConfig}
                      onSort={handleSortOrder}
                    >
                      {t("admin.date")}
                    </ThSortable>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length > 0 ? (
                    filteredOrders.map((o) => (
                      <tr key={o.id} className="border-b border-border/60">
                        <td className="py-2 font-mono text-xs">{o.id}</td>
                        <td className="py-2">
                          <OrderTypeCell order={o} />
                        </td>
                        <td className="py-2 text-muted-foreground">{o.user}</td>
                        <td className="py-2 font-semibold">{format(o.paid ?? o.total)}</td>
                        <td className="py-2 text-muted-foreground">
                          {o.cost != null ? format(o.cost) : "—"}
                        </td>
                        <td
                          className={`py-2 font-semibold ${
                            o.profit == null
                              ? "text-muted-foreground"
                              : o.profit < 0
                                ? "text-red-500"
                                : o.profit > 0
                                  ? "text-emerald-500"
                                  : ""
                          }`}
                        >
                          {o.profit != null ? format(o.profit) : "—"}
                        </td>
                        <td className="py-2">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                              o.status === "completed"
                                ? "bg-green-500/15 text-green-500"
                                : o.status === "pending"
                                  ? "bg-yellow-500/15 text-yellow-500"
                                  : "bg-destructive/15 text-destructive"
                            }`}
                          >
                            {o.status}
                          </span>
                        </td>
                        <td className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                          {formatServerDateTime(o.created_at)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="py-4 text-center text-muted-foreground">
                        {t("admin.no_orders")}
                      </td>
                    </tr>
                  )}
                </tbody>
                {filteredOrders.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-border font-semibold">
                      <td className="py-2" colSpan={3}>
                        {t("admin.totals")}
                        {orderTotals.unknownCost > 0 && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            {t("admin.totals_unknown_cost", { count: orderTotals.unknownCost })}
                          </span>
                        )}
                      </td>
                      <td className="py-2">{format(orderTotals.paid)}</td>
                      <td className="py-2 text-muted-foreground">{format(orderTotals.cost)}</td>
                      <td
                        className={`py-2 ${orderTotals.profit < 0 ? "text-red-500" : "text-emerald-500"}`}
                      >
                        {format(orderTotals.profit)}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ── Tickets Tab ── */}
        <TabsContent value="tickets">
          <div className="grid gap-6 lg:grid-cols-5">
            <div className="lg:col-span-2 space-y-2">
              {adminTickets.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border p-10 text-center">
                  <LifeBuoy className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
                  <p className="text-muted-foreground">{t("tickets.no_tickets")}</p>
                </div>
              ) : (
                adminTickets.map((tItem) => (
                  <button
                    key={tItem.id}
                    onClick={() => setSelectedTicket(tItem)}
                    className={`w-full rounded-xl border p-4 text-left transition-all hover:border-primary/40 ${
                      selectedTicket?.id === tItem.id
                        ? "border-primary/50 bg-primary/5"
                        : "border-border bg-card"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-sm">{tItem.subject}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          #{tItem.id} · {tItem.contact_email}
                        </p>
                        <span
                          className={`mt-1 inline-block rounded border px-1.5 py-0.5 text-[10px] font-medium ${
                            tItem.is_guest
                              ? "border-orange-500/30 bg-orange-500/10 text-orange-400"
                              : "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                          }`}
                        >
                          {tItem.is_guest ? t("admin.ticket_guest") : t("admin.ticket_registered")}
                        </span>
                      </div>
                      <span
                        className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${
                          tItem.status === "open"
                            ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                            : tItem.status === "in_progress"
                              ? "bg-blue-500/15 text-blue-400 border-blue-500/30"
                              : "bg-muted text-muted-foreground border-border"
                        }`}
                      >
                        {tItem.status === "open"
                          ? t("admin.open")
                          : tItem.status === "in_progress"
                            ? t("admin.in_progress")
                            : t("admin.closed")}
                      </span>
                    </div>
                    {tItem.messages?.length > 0 && (
                      <p className="mt-2 line-clamp-1 text-xs text-muted-foreground">
                        {tItem.messages[tItem.messages.length - 1].content}
                      </p>
                    )}
                  </button>
                ))
              )}
            </div>

            <div className="lg:col-span-3">
              {selectedTicket ? (
                <div
                  className="flex flex-col rounded-2xl border border-border bg-card overflow-hidden"
                  style={{ minHeight: 450 }}
                >
                  <div className="border-b border-border px-5 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold">{selectedTicket.subject}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          #{selectedTicket.id} · {selectedTicket.contact_email}
                        </p>
                        {/* Answering a guest emails them the whole thread and accepts
                            their reply back; answering a member only pings them. Worth
                            saying before the reply is typed, not after. */}
                        <p
                          className={`mt-1.5 inline-block rounded-md border px-2 py-1 text-[11px] leading-snug ${
                            selectedTicket.is_guest
                              ? "border-orange-500/30 bg-orange-500/10 text-orange-300"
                              : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                          }`}
                        >
                          <strong>
                            {selectedTicket.is_guest
                              ? t("admin.ticket_guest")
                              : t("admin.ticket_registered")}
                          </strong>{" "}
                          —{" "}
                          {selectedTicket.is_guest
                            ? t("admin.ticket_guest_hint")
                            : t("admin.ticket_user_hint")}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {["open", "in_progress", "closed"].map((st) => (
                          <button
                            key={st}
                            onClick={async () => {
                              try {
                                await api.patch(`/api/admin/tickets/${selectedTicket.id}/status`, {
                                  status: st,
                                });
                                toast.success(t("admin.saved"));
                                fetchAll();
                                setSelectedTicket((prev: any) => prev && { ...prev, status: st });
                              } catch (e) {
                                toast.error(e instanceof ApiError ? e.message : t("common.error"));
                              }
                            }}
                            className={`rounded-lg px-2 py-1 text-xs font-medium transition-colors ${
                              selectedTicket.status === st
                                ? "bg-primary text-primary-foreground"
                                : "border border-border bg-background hover:bg-accent"
                            }`}
                          >
                            {t(
                              st === "open"
                                ? "admin.open"
                                : st === "in_progress"
                                  ? "admin.in_progress"
                                  : "admin.closed",
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div
                    ref={ticketMessagesBoxRef}
                    className="flex-1 overflow-y-auto p-5 space-y-4"
                    style={{ maxHeight: 360 }}
                  >
                    {(selectedTicket.messages || []).map((m: any) => {
                      const isAdmin = m.sender_role === "admin";
                      return (
                        <div
                          key={m.id}
                          className={`flex gap-3 ${isAdmin ? "" : "flex-row-reverse"}`}
                        >
                          <div
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isAdmin ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}
                          >
                            {isAdmin ? (
                              <Shield className="h-4 w-4" />
                            ) : (
                              <UserIcon className="h-4 w-4" />
                            )}
                          </div>
                          <div className={`max-w-[80%] ${isAdmin ? "" : "items-end"}`}>
                            <div
                              className={`rounded-2xl px-4 py-2.5 text-sm ${isAdmin ? "bg-primary/10 text-foreground rounded-tl-sm" : "bg-muted text-foreground rounded-tr-sm"}`}
                            >
                              {m.content}
                            </div>
                            <p
                              className={`mt-1 text-xs text-muted-foreground ${isAdmin ? "text-left" : "text-right"}`}
                            >
                              {/* Seen from the admin's side: our own messages are "you",
                                  the customer's carry their address. The labels used to be
                                  copied from the customer view, which called them "you". */}
                              {isAdmin
                                ? t("admin.ticket_you_admin")
                                : selectedTicket.is_guest
                                  ? `${t("admin.ticket_guest")} · ${selectedTicket.contact_email}`
                                  : selectedTicket.contact_email || t("admin.ticket_customer")}{" "}
                              · {formatServerDateTime(m.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={ticketMessagesEndRef} />
                  </div>

                  {selectedTicket.status !== "closed" && (
                    <div className="border-t border-border p-4">
                      <div className="flex gap-2">
                        <textarea
                          placeholder={t("admin.reply_placeholder")}
                          value={ticketReply}
                          onChange={(e) => setTicketReply(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              (async () => {
                                if (!ticketReply.trim()) return;
                                await sendTicketReply();
                              })();
                            }
                          }}
                          rows={2}
                          className="flex-1 rounded-xl border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                        />
                        <Button
                          onClick={async () => {
                            if (!ticketReply.trim()) return;
                            await sendTicketReply();
                          }}
                          disabled={ticketReplying || !ticketReply.trim()}
                          className="bg-gradient-primary shadow-glow self-end"
                          size="icon"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex h-full min-h-[350px] items-center justify-center rounded-2xl border border-dashed border-border">
                  <div className="text-center">
                    <MessageCircle className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
                    <p className="text-muted-foreground">{t("tickets.select_ticket")}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ── Purchased Accounts Tab ── */}
        <TabsContent value="accounts">
          <Card className="border-border bg-card p-4 sm:p-6">
            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <table className="w-full text-sm min-w-[750px]">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <ThSortable
                      sortKey="id"
                      currentSort={accountSortConfig}
                      onSort={handleSortAccount}
                    >
                      ID
                    </ThSortable>
                    <ThSortable
                      sortKey="user"
                      currentSort={accountSortConfig}
                      onSort={handleSortAccount}
                    >
                      {t("admin.user")}
                    </ThSortable>
                    <ThSortable
                      sortKey="account_type"
                      currentSort={accountSortConfig}
                      onSort={handleSortAccount}
                    >
                      {t("admin.account_type")}
                    </ThSortable>
                    <ThSortable
                      sortKey="credentials"
                      currentSort={accountSortConfig}
                      onSort={handleSortAccount}
                    >
                      {t("admin.credentials")}
                    </ThSortable>
                    <ThSortable
                      sortKey="notes"
                      currentSort={accountSortConfig}
                      onSort={handleSortAccount}
                    >
                      {t("admin.admin_notes")}
                    </ThSortable>
                    <ThSortable
                      sortKey="status"
                      currentSort={accountSortConfig}
                      onSort={handleSortAccount}
                    >
                      {t("admin.status")}
                    </ThSortable>
                    <ThSortable
                      sortKey="date"
                      currentSort={accountSortConfig}
                      onSort={handleSortAccount}
                    >
                      {t("admin.date")}
                    </ThSortable>
                    <th className="pb-2">{t("admin.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.length > 0 ? (
                    filteredAccounts.map((a) => (
                      <tr key={a.id} className="border-b border-border/60">
                        <td className="py-2 text-xs font-mono">#{a.id}</td>
                        <td className="py-2">{a.username || `User ${a.user_id}`}</td>
                        <td className="py-2 font-medium">{a.account_type}</td>
                        <td className="py-2 pr-2">
                          <Input
                            value={a.account_data || ""}
                            placeholder={t("admin.credentials_ph")}
                            onChange={(e) => {
                              const val = e.target.value;
                              setAdminAccounts((prev) =>
                                prev.map((item) =>
                                  item.id === a.id ? { ...item, account_data: val } : item,
                                ),
                              );
                            }}
                            onBlur={(e) => {
                              void api
                                .patch(`/api/admin/purchased-accounts/${a.id}`, {
                                  account_data: e.target.value,
                                })
                                .catch(() => toast.error(t("common.error")));
                            }}
                            className="w-full min-w-[180px]"
                          />
                        </td>
                        <td className="py-2 pr-2">
                          <Input
                            value={a.notes || ""}
                            placeholder={t("admin.internal_notes")}
                            onChange={(e) => {
                              const val = e.target.value;
                              setAdminAccounts((prev) =>
                                prev.map((item) =>
                                  item.id === a.id ? { ...item, notes: val } : item,
                                ),
                              );
                            }}
                            onBlur={(e) => {
                              void api
                                .patch(`/api/admin/purchased-accounts/${a.id}`, {
                                  notes: e.target.value,
                                })
                                .catch(() => toast.error(t("common.error")));
                            }}
                            className="w-full min-w-[140px]"
                          />
                        </td>
                        <td className="py-2">
                          <select
                            className="bg-background border border-input rounded text-sm px-2 py-1"
                            value={a.status}
                            onChange={(e) => {
                              const newStatus = e.target.value;
                              setAdminAccounts((prev) =>
                                prev.map((item) =>
                                  item.id === a.id ? { ...item, status: newStatus } : item,
                                ),
                              );
                              void api
                                .patch(`/api/admin/purchased-accounts/${a.id}`, {
                                  status: newStatus,
                                })
                                .then(() => {
                                  toast.success(t("admin.saved"));
                                  fetchAll();
                                })
                                .catch(() => toast.error(t("common.error")));
                            }}
                          >
                            <option value="pending">{t("admin.pending")}</option>
                            <option value="delivered">{t("admin.delivered_f")}</option>
                            <option value="cancelled">{t("admin.cancelled_f")}</option>
                          </select>
                        </td>
                        <td className="py-2 text-xs text-muted-foreground">
                          {formatServerDateTime(a.created_at)}
                        </td>
                        <td className="py-2">
                          {a.status === "pending" ? (
                            <Button
                              size="sm"
                              onClick={() => setDeliveringAccount(a)}
                              className="bg-gradient-primary shadow-glow text-xs flex items-center gap-1.5 h-7 px-2.5"
                            >
                              <Send className="h-3 w-3" />
                              {t("admin.send_account")}
                            </Button>
                          ) : (
                            <span className="text-xs text-emerald-400 font-medium flex items-center gap-1">
                              <CheckCircle2 className="h-3.5 w-3.5" /> {t("admin.delivered")}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="py-4 text-center text-muted-foreground">
                        {t("admin.no_purchased_accounts")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ── Account Products (Catalog) Tab ── */}
        <TabsContent value="account_products">
          <Card className="border-border bg-card p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">{t("admin.account_products")}</h2>
                <p className="text-xs text-muted-foreground">{t("admin.products_hint")}</p>
              </div>
              <Button
                onClick={() => setShowAddProductModal(true)}
                size="sm"
                className="bg-gradient-primary shadow-glow flex items-center gap-1.5"
              >
                <Plus className="h-4 w-4" /> {t("admin.add_product")}
              </Button>
            </div>
            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <table className="w-full text-sm min-w-[750px]">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <ThSortable
                      sortKey="code"
                      currentSort={productSortConfig}
                      onSort={handleSortProduct}
                    >
                      {t("admin.code")}
                    </ThSortable>
                    <ThSortable
                      sortKey="name"
                      currentSort={productSortConfig}
                      onSort={handleSortProduct}
                    >
                      {t("admin.name_label")}
                    </ThSortable>
                    <ThSortable
                      sortKey="price_usd"
                      currentSort={productSortConfig}
                      onSort={handleSortProduct}
                    >
                      {t("admin.price")}
                    </ThSortable>
                    <ThSortable
                      sortKey="stock"
                      currentSort={productSortConfig}
                      onSort={handleSortProduct}
                    >
                      {t("admin.stock")}
                    </ThSortable>
                    <ThSortable
                      sortKey="has_followers"
                      currentSort={productSortConfig}
                      onSort={handleSortProduct}
                    >
                      {t("admin.followers_q")}
                    </ThSortable>
                    <th>{t("admin.icon")}</th>
                    <th className="pb-2">{t("admin.color")}</th>
                    <th className="pb-2">{t("admin.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((p) => (
                    <tr key={p.id} className="border-b border-border/60">
                      <td className="py-2 font-mono text-xs">{p.code}</td>
                      <td className="py-2">
                        <Input
                          defaultValue={p.name}
                          onBlur={(e) =>
                            e.target.value !== p.name &&
                            handleProductUpdate(p.id, "name", e.target.value)
                          }
                          className="h-8 min-w-[140px]"
                        />
                      </td>
                      <td className="py-2">
                        <Input
                          type="number"
                          step="0.01"
                          defaultValue={p.price_usd}
                          onBlur={(e) =>
                            parseFloat(e.target.value) !== p.price_usd &&
                            handleProductUpdate(p.id, "price_usd", parseFloat(e.target.value))
                          }
                          className="h-8 w-24"
                        />
                      </td>
                      <td className="py-2">
                        <Input
                          type="number"
                          defaultValue={p.stock}
                          onBlur={(e) =>
                            parseInt(e.target.value) !== p.stock &&
                            handleProductUpdate(p.id, "stock", parseInt(e.target.value))
                          }
                          className="h-8 w-24"
                        />
                      </td>
                      <td className="py-2">
                        <select
                          className="h-8 rounded-md border border-input bg-background px-3 text-sm"
                          defaultValue={p.has_followers ? "1" : "0"}
                          onChange={(e) =>
                            handleProductUpdate(p.id, "has_followers", e.target.value === "1")
                          }
                        >
                          <option value="1">{t("common.yes")}</option>
                          <option value="0">{t("common.no")}</option>
                        </select>
                      </td>
                      <td className="py-2">
                        <div className="flex items-center gap-1.5">
                          <SocialIcon
                            name={p.icon_name}
                            className="h-5 w-5 shrink-0"
                            color={p.color}
                          />
                          <select
                            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                            defaultValue={p.icon_name || "Instagram"}
                            onChange={(e) => handleProductUpdate(p.id, "icon_name", e.target.value)}
                          >
                            <option value="Instagram">Instagram</option>
                            <option value="TikTok">TikTok</option>
                            <option value="Facebook">Facebook</option>
                            <option value="Twitter">Twitter / X</option>
                            <option value="YouTube">YouTube</option>
                            <option value="Telegram">Telegram</option>
                            <option value="Gmail">Gmail</option>
                            <option value="Discord">Discord</option>
                          </select>
                        </div>
                      </td>
                      <td className="py-2">
                        <Input
                          type="color"
                          defaultValue={p.color}
                          onBlur={(e) =>
                            e.target.value !== p.color &&
                            handleProductUpdate(p.id, "color", e.target.value)
                          }
                          className="h-8 w-12 p-1"
                        />
                      </td>
                      <td className="py-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleProductDelete(p.id)}
                          className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ── Referrals Settings Tab ── */}
        <TabsContent value="referrals">
          <Card className="border-border bg-card p-4 sm:p-6 space-y-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Share2 className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{t("admin.referral_settings")}</h2>
                <p className="text-xs text-muted-foreground">{t("admin.referral_settings_hint")}</p>
              </div>
            </div>

            <form onSubmit={handleSaveRefSettings} className="space-y-4 max-w-lg">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("admin.referral_reward")}</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={refSettings.referral_reward_amount}
                    onChange={(e) =>
                      setRefSettings({
                        ...refSettings,
                        referral_reward_amount: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-36 font-semibold"
                  />
                  <span className="text-sm text-muted-foreground">
                    {t("admin.referral_reward_hint")}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">{t("admin.referral_min_recharge")}</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={refSettings.referral_min_recharge}
                    onChange={(e) =>
                      setRefSettings({
                        ...refSettings,
                        referral_min_recharge: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-36 font-semibold"
                  />
                  <span className="text-sm text-muted-foreground">
                    {t("admin.referral_min_recharge_hint")}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">{t("admin.referral_min_withdrawal")}</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={refSettings.referral_min_withdrawal}
                    onChange={(e) => {
                      // Number(), not `|| 0` on a parseFloat: writing 0 is a valid
                      // setting ("sin mínimo"), and `|| 0` would silently discard it.
                      const parsed = Number(e.target.value);
                      setRefSettings({
                        ...refSettings,
                        referral_min_withdrawal:
                          Number.isFinite(parsed) && parsed >= 0 ? parsed : 0,
                      });
                    }}
                    className="w-36 font-semibold"
                  />
                  <span className="text-sm text-muted-foreground">
                    {t("admin.referral_min_withdraw_hint")}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="ref-enabled-check"
                  checked={refSettings.referral_enabled}
                  onChange={(e) =>
                    setRefSettings({ ...refSettings, referral_enabled: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                <label htmlFor="ref-enabled-check" className="text-sm font-medium cursor-pointer">
                  {t("admin.referral_enabled")}
                </label>
              </div>

              <div className="pt-3">
                <Button
                  type="submit"
                  disabled={savingRefSettings}
                  className="bg-gradient-primary shadow-glow"
                >
                  {savingRefSettings ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      {t("admin.saving")}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {t("admin.save_settings")}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Card>
        </TabsContent>

        {/* ── Referral Withdrawals Tab ── */}
        <TabsContent value="referral_withdrawals">
          <Card className="border-border bg-card p-4 sm:p-6 space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Wallet className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{t("admin.referral_withdrawals")}</h2>
                  <p className="text-xs text-muted-foreground">{t("admin.withdrawals_hint")}</p>
                </div>
              </div>
              <select
                value={withdrawalFilter}
                onChange={(e) => {
                  setWithdrawalFilter(e.target.value);
                  void loadWithdrawals(e.target.value);
                }}
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                aria-label={t("admin.filter_by_status")}
              >
                <option value="pending">{t("admin.pending_plural")}</option>
                <option value="paid">{t("admin.paid_plural")}</option>
                <option value="rejected">{t("admin.rejected_plural")}</option>
                <option value="all">{t("admin.all")}</option>
              </select>
            </div>

            {withdrawals.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {t("admin.no_requests_in_state")}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">{t("admin.date")}</th>
                      <th className="pb-2 pr-4 font-medium">{t("admin.user_label")}</th>
                      <th className="pb-2 pr-4 font-medium">{t("admin.amount")}</th>
                      <th className="pb-2 pr-4 font-medium">{t("admin.wallet_trc20")}</th>
                      <th className="pb-2 pr-4 font-medium">{t("admin.status_label")}</th>
                      <th className="pb-2 font-medium">{t("admin.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawals.map((w) => (
                      <tr key={w.id} className="border-b border-border/50 last:border-0">
                        <td className="whitespace-nowrap py-3 pr-4 text-muted-foreground">
                          {formatServerDateTime(w.created_at)}
                        </td>
                        <td className="py-3 pr-4">
                          <p className="font-medium">{w.username}</p>
                          <p className="text-xs text-muted-foreground">{w.email}</p>
                        </td>
                        <td className="py-3 pr-4 font-semibold">{format(w.amount)}</td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <code className="max-w-[14rem] truncate font-mono text-xs">
                              {w.wallet_address}
                            </code>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              onClick={() => void copyToClipboard(w.wallet_address)}
                              aria-label={t("admin.copy_wallet")}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                        <td className="py-3 pr-4">
                          <span
                            className={`inline-block rounded-full border px-2 py-0.5 text-xs ${
                              w.status === "paid"
                                ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-500"
                                : w.status === "rejected"
                                  ? "border-destructive/30 bg-destructive/15 text-destructive"
                                  : "border-amber-500/30 bg-amber-500/15 text-amber-500"
                            }`}
                          >
                            {w.status}
                          </span>
                          {w.admin_note && (
                            <p className="mt-1 max-w-[12rem] text-xs text-muted-foreground">
                              {w.admin_note}
                            </p>
                          )}
                        </td>
                        <td className="py-3">
                          {w.status === "pending" ? (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                disabled={processingWithdrawal === w.id}
                                onClick={() => void handleWithdrawalAction(w.id, "approve")}
                              >
                                {t("admin.paid")}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={processingWithdrawal === w.id}
                                onClick={() => void handleWithdrawalAction(w.id, "reject")}
                              >
                                {t("admin.reject")}
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {formatServerDateTime(w.processed_at)}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Añadir Producto de Cuenta */}
      <Dialog open={showAddProductModal} onOpenChange={setShowAddProductModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("admin.add_account_product")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleProductCreate} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold">{t("admin.unique_slug")}</label>
              <Input
                placeholder={t("common.example_value", { value: "tiktok_creator" })}
                value={newProduct.code}
                onChange={(e) =>
                  setNewProduct({
                    ...newProduct,
                    code: e.target.value.toLowerCase().replace(/\s+/g, "_"),
                  })
                }
                required
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold">{t("admin.product_name")}</label>
              <Input
                placeholder={t("common.example_value", { value: "TikTok — High Reach Account" })}
                value={newProduct.name}
                onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold">{t("admin.description")}</label>
              <Input
                placeholder={t("admin.product_desc_ph")}
                value={newProduct.description}
                onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold">{t("admin.base_price")}</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.1"
                  value={newProduct.price_usd}
                  onChange={(e) =>
                    setNewProduct({ ...newProduct, price_usd: parseFloat(e.target.value) || 0 })
                  }
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold">{t("admin.initial_stock")}</label>
                <Input
                  type="number"
                  min="0"
                  value={newProduct.stock}
                  onChange={(e) =>
                    setNewProduct({ ...newProduct, stock: parseInt(e.target.value) || 0 })
                  }
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold">{t("admin.official_icon")}</label>
                <div className="flex items-center gap-2">
                  <SocialIcon
                    name={newProduct.icon_name}
                    className="h-6 w-6 shrink-0"
                    color={newProduct.color}
                  />
                  <select
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-xs"
                    value={newProduct.icon_name}
                    onChange={(e) => setNewProduct({ ...newProduct, icon_name: e.target.value })}
                  >
                    <option value="Instagram">Instagram</option>
                    <option value="TikTok">TikTok</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Twitter">Twitter / X</option>
                    <option value="YouTube">YouTube</option>
                    <option value="Telegram">Telegram</option>
                    <option value="Gmail">Gmail</option>
                    <option value="Discord">Discord</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold">{t("admin.brand_colour")}</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="color"
                    value={newProduct.color}
                    onChange={(e) => setNewProduct({ ...newProduct, color: e.target.value })}
                    className="h-9 w-12 p-1 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={newProduct.color}
                    onChange={(e) => setNewProduct({ ...newProduct, color: e.target.value })}
                    className="h-9 font-mono text-xs"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <input
                type="checkbox"
                id="new-has-followers"
                checked={newProduct.has_followers}
                onChange={(e) => setNewProduct({ ...newProduct, has_followers: e.target.checked })}
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              <label htmlFor="new-has-followers" className="text-xs font-semibold cursor-pointer">
                {t("admin.allow_followers")}
              </label>
            </div>
            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAddProductModal(false)}
              >
                {t("admin.cancel")}
              </Button>
              <Button type="submit" size="sm" className="bg-gradient-primary shadow-glow">
                {t("admin.create_product")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UserRow({
  user,
  onUpdate,
  format,
  t,
}: {
  user: any;
  onUpdate: Function;
  format: Function;
  t: Function;
}) {
  const [balanceAdd, setBalanceAdd] = useState("");
  const [loading, setLoading] = useState(false);

  return (
    <tr className="border-b border-border/60">
      <td className="py-2 font-medium">{user.username}</td>
      <td className="py-2 text-xs text-muted-foreground">{user.email}</td>
      <td className="py-2 font-semibold text-primary">{format(user.balance)}</td>
      <td className="py-2">
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium ${user.role === "admin" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}
        >
          {user.role === "admin" ? `👑 ${t("admin.role_admin")}` : t("admin.role_user")}
        </span>
        {user.is_banned && (
          <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-destructive/15 text-destructive">
            {t("admin.banned")}
          </span>
        )}
      </td>
      <td className="py-2">
        <button
          onClick={async () => {
            setLoading(true);
            await onUpdate(user.id, { is_email_verified: !user.is_email_verified });
            setLoading(false);
          }}
          disabled={loading}
          title={user.is_email_verified ? t("admin.revoke_verification") : t("admin.mark_verified")}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all border cursor-pointer ${
            user.is_email_verified
              ? "bg-green-500/15 text-green-400 border-green-500/30 hover:bg-red-500/15 hover:text-red-400 hover:border-red-500/30"
              : "bg-yellow-500/15 text-yellow-400 border-yellow-500/30 hover:bg-green-500/15 hover:text-green-400 hover:border-green-500/30"
          }`}
        >
          {loading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : user.is_email_verified ? (
            <>
              <CheckCircle2 className="h-3 w-3" />
              {t("admin.verified")}
            </>
          ) : (
            <>
              <Clock className="h-3 w-3" /> {t("admin.pending")}
            </>
          )}
        </button>
      </td>
      <td className="py-2">
        <div className="flex items-center gap-1">
          <Input
            type="number"
            step="0.01"
            placeholder="0.00"
            value={balanceAdd}
            onChange={(e) => setBalanceAdd(e.target.value)}
            className="w-20 sm:w-24"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const val = parseFloat(balanceAdd);
              if (!isNaN(val) && val !== 0) {
                onUpdate(user.id, { balance_add: val });
                setBalanceAdd("");
              }
            }}
          >
            +
          </Button>
        </div>
      </td>
      <td className="py-2">
        <div className="flex flex-col gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs justify-start"
            onClick={() => onUpdate(user.id, { role: user.role === "admin" ? "user" : "admin" })}
          >
            <Shield className="h-3 w-3 mr-1" />
            {user.role === "admin" ? t("admin.remove_admin") : t("admin.make_admin")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs justify-start text-destructive hover:text-destructive"
            onClick={() => onUpdate(user.id, { is_banned: !user.is_banned })}
          >
            <AlertCircle className="h-3 w-3 mr-1" />
            {user.is_banned ? t("admin.unban") : t("admin.ban")}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function SendAccountModal({
  account,
  onClose,
  onDelivered,
}: {
  account: any;
  onClose: () => void;
  onDelivered: () => void;
}) {
  const { t } = useI18n();
  const [text, setText] = useState(account.account_data || "");
  const [sending, setSending] = useState(false);

  async function handleSend() {
    if (!text.trim()) {
      toast.error(t("admin.enter_credentials"));
      return;
    }
    setSending(true);
    try {
      await api.post(`/api/admin/purchased-accounts/${account.id}/deliver`, {
        account_data: text,
      });
      toast.success(t("admin.account_sent_success"));
      onDelivered();
      onClose();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("admin.error_send_account"));
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-2xl border border-border bg-card shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-border bg-gradient-to-r from-primary/10 to-transparent">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/20 text-primary">
              <Send className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-bold">{t("admin.send_account_title")}</h3>
              <p className="text-xs text-muted-foreground">
                #{account.id} · {account.account_type} (
                {account.username || `User ${account.user_id}`})
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-xl border border-border bg-muted/40 p-3 text-xs text-muted-foreground space-y-1">
            <p>
              <strong className="text-foreground">{t("admin.user")}:</strong>{" "}
              {account.username || `User ${account.user_id}`}
            </p>
            <p>
              <strong className="text-foreground">{t("admin.account_type")}:</strong>{" "}
              {account.account_type}
            </p>
            <p>
              <strong className="text-foreground">{t("admin.total")}:</strong> $
              {account.price?.toFixed(2)}
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5 text-foreground">
              {t("admin.send_account_desc")}
            </label>
            <textarea
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("admin.send_account_placeholder")}
              className="w-full rounded-xl border border-input bg-background p-3 text-sm font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-border bg-muted/20">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={sending}>
            {t("common.cancel")}
          </Button>
          <Button
            size="sm"
            onClick={handleSend}
            disabled={sending || !text.trim()}
            className="bg-gradient-primary shadow-glow flex items-center gap-1.5"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {t("admin.send_account_submit")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ThSortable({
  children,
  sortKey,
  currentSort,
  onSort,
  className = "",
}: {
  children: React.ReactNode;
  sortKey: string;
  currentSort: { key: string | null; dir: "asc" | "desc" | null };
  onSort: (key: string) => void;
  className?: string;
}) {
  const active = currentSort.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`pb-2 cursor-pointer select-none hover:text-foreground transition-colors ${className}`}
    >
      <div className="inline-flex items-center gap-1">
        <span>{children}</span>
        {active ? (
          currentSort.dir === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5 text-primary shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 text-primary shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 text-muted-foreground/30 shrink-0 hover:text-muted-foreground" />
        )}
      </div>
    </th>
  );
}

/** "SMS · ds" plus the human name, so a code like `ds` reads as Discord. */
type OrderTypeInfo = {
  type?: string;
  activation_type?: string;
  service_code?: string;
  service_name?: string | null;
  target_site?: string | null;
  channel?: string;
};

function OrderTypeCell({ order }: { order: OrderTypeInfo }) {
  const { t } = useI18n();
  const kind = (order.activation_type || "sms").toLowerCase();
  if (!order.service_code) return <>{order.type}</>;
  const name =
    kind === "email"
      ? order.target_site
        ? t("admin.for_site", { site: order.target_site })
        : null
      : order.service_name || getServiceName(order.service_code, "sms");
  const showName = name && name.toLowerCase() !== String(order.service_code).toLowerCase();
  return (
    <div className="flex flex-col">
      <span>
        {kind.toUpperCase()} · <span className="font-mono text-xs">{order.service_code}</span>
        {order.channel === "api" && (
          <span className="ml-1.5 rounded border border-primary/30 bg-primary/10 px-1 py-0.5 text-[10px] font-medium text-primary">
            API
          </span>
        )}
      </span>
      {showName && <span className="text-xs text-muted-foreground">({name})</span>}
    </div>
  );
}
